/**
 * Extracts error information from backend responses
 * Handles dual-property backend DTOs (legacy + new structure)
 */

import { CLIENT_ERROR_CODES } from '../constants/clientErrorCodes';

export interface ExtractedError {
  code: string;
  message: string;
  field?: string;
}

/**
 * Extracts errors from backend response data
 * Prioritizes new errorDetails structure over legacy string arrays
 * 
 * Backend DTOs have dual properties:
 * - errorDetails: Array<{ code: string; message: string; field?: string }> (new)
 * - errors: string[] (legacy, for G1App compatibility)
 * - errorMessages: string[] (FormOperationResult legacy)
 * 
 * @param responseData - Backend response object
 * @returns Array of extracted errors with codes and messages
 */
export function extractBackendErrors(responseData: unknown): ExtractedError[] {
  if (!responseData || typeof responseData !== 'object') {
    return [];
  }

  const response = responseData as Record<string, unknown>;

  // Priority 1: Check errorDetails (new structured format)
  if (Array.isArray(response.errorDetails) && response.errorDetails.length > 0) {
    return response.errorDetails
      .filter(isValidErrorDetail)
      .map((detail) => ({
        code: detail.code,
        message: detail.message,
        field: detail.field,
      }));
  }

  // Priority 2: Check errors array (legacy string array from OperationResultDTO)
  if (Array.isArray(response.errors) && response.errors.length > 0) {
    return response.errors
      .filter((err): err is string => typeof err === 'string')
      .map((message) => ({
        code: CLIENT_ERROR_CODES.UNKNOWN_ERROR, // No code available in legacy format
        message,
      }));
  }

  // Priority 3: Check errorMessages (FormOperationResult legacy)
  if (Array.isArray(response.errorMessages) && response.errorMessages.length > 0) {
    return response.errorMessages
      .filter((err): err is string => typeof err === 'string')
      .map((message) => ({
        code: CLIENT_ERROR_CODES.UNKNOWN_ERROR,
        message,
      }));
  }

  // No errors found
  return [];
}

/**
 * Type guard to validate error detail structure
 */
function isValidErrorDetail(
  detail: unknown
): detail is { code: string; message: string; field?: string } {
  if (!detail || typeof detail !== 'object') {
    return false;
  }

  const obj = detail as Record<string, unknown>;
  return typeof obj.code === 'string' && typeof obj.message === 'string';
}

/**
 * Extracts a single error from backend response (first error found)
 * Useful when you only need to display one error
 * 
 * @param responseData - Backend response object
 * @returns First extracted error or null
 */
export function extractFirstBackendError(responseData: unknown): ExtractedError | null {
  const errors = extractBackendErrors(responseData);
  return errors.length > 0 ? errors[0] : null;
}

/**
 * Checks if backend response indicates success
 * Checks both success property and absence of errors
 * 
 * @param responseData - Backend response object
 * @returns True if response is successful
 */
export function isBackendResponseSuccess(responseData: unknown): boolean {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }

  const response = responseData as Record<string, unknown>;

  // Check explicit success property
  if (typeof response.success === 'boolean') {
    return response.success;
  }

  // Check if no errors are present
  const errors = extractBackendErrors(responseData);
  return errors.length === 0;
}

/**
 * Extracts validation errors organized by field
 * Useful for form error display
 * 
 * @param responseData - Backend response object
 * @returns Map of field names to error messages
 */
export function extractValidationErrorsByField(
  responseData: unknown
): Map<string, string[]> {
  const errors = extractBackendErrors(responseData);
  const fieldErrors = new Map<string, string[]>();

  errors.forEach((error) => {
    if (error.field) {
      const existing = fieldErrors.get(error.field) || [];
      existing.push(error.message);
      fieldErrors.set(error.field, existing);
    }
  });

  return fieldErrors;
}
